import { motion } from 'framer-motion';
import type { ReactNode } from 'react';

import { easeOut } from './motion';

interface StepHeaderProps {
  step: number;
  totalSteps: number;
  title: ReactNode;
  subtitle: ReactNode;
}

/** Portal page header with a segmented progress strip beside the eyebrow. */
export const StepHeader = ({
  step,
  totalSteps,
  title,
  subtitle,
}: StepHeaderProps) => (
  <div>
    <div className="mb-3 flex items-center gap-3">
      <p className="text-muted-foreground text-[13px] font-medium tabular-nums">
        Step {step} of {totalSteps}
      </p>
      <div
        className="flex gap-1"
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={totalSteps}
        aria-valuenow={step}
      >
        {Array.from({ length: totalSteps }, (_, index) => (
          <span
            key={index}
            className="relative h-1 w-6 overflow-hidden rounded-full bg-neutral-200"
          >
            <motion.span
              className="bg-primary absolute inset-0 origin-left"
              initial={false}
              animate={{ scaleX: index < step ? 1 : 0 }}
              transition={{
                duration: 0.25,
                ease: easeOut,
                delay: index === step - 1 ? 0.15 : 0,
              }}
            />
          </span>
        ))}
      </div>
    </div>
    <h1 className="text-[24px] leading-8 font-semibold tracking-[-0.02em] text-balance">
      {title}
    </h1>
    <p className="text-muted-foreground mt-1 text-[13px]">{subtitle}</p>
  </div>
);
